import { Injector, InjectionToken, Injectable, ComponentFactoryResolver, Type, ApplicationRef } from '@angular/core';
import { IDialog, DialogRef } from './dialog-ref';

export type SlDialogRef = HTMLElement & {
  hide: () => Promise<void>;
  show: () => Promise<void>;
}
export const DIALOG = new InjectionToken<string>('dialog');
export const DATA = new InjectionToken<string>('data');

export interface IDialogService {
  open(component: Type<any>, title: string, options?: Options): IDialog;
}

export interface Options {
  data?: any;
  width?: string;
  headerStyle?: 'normal' | 'discreet' | 'hidden';
  padding?: 'normal' | 'none';
}

@Injectable()
export class DialogService implements IDialogService {
  constructor(
    private injector: Injector,
    private resolver: ComponentFactoryResolver,
    private appRef: ApplicationRef
  ) { }

  /**
   * Opens a modal containing the specified Component. The component can inject a ModalRef in its constructor to get a reference to the modal.
   * Having the ModalRef in the component allows the component to close the dialog with a result.
   * @param componentType The type of angular component.
   * @returns A reference to the dialog.
   */
  open(componentType: Type<any>, title: string, options?: Options): IDialog {
    // Create modal element and a DialogRef wrapper for it.
    const modalRef = this.createSlDialog(title, options);
    const dialogRef = new DialogRef(modalRef as SlDialogRef);

    // Instantiates the Anguar component and appends it to the modal.
    const injector = Injector.create({providers: [{provide: DIALOG, useValue: dialogRef}, {provide: DATA, useValue: options?.data}], parent: this.injector});
    const componentRef = this.resolver.resolveComponentFactory(componentType).create(injector);
    componentRef.changeDetectorRef.detectChanges();

    this.appRef.attachView(componentRef.hostView);
    modalRef.appendChild(componentRef.location.nativeElement);

    // Display the modal and return ref to caller
    this.displayWhenHydrated(modalRef);

    dialogRef.afterClosed().subscribe(() => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    });
    
    return dialogRef;
  }

  private displayWhenHydrated(modalRef: SlDialogRef) {
    const ttl = 200;
    const start = new Date();
    const interval = setInterval(() => {
      const now = new Date();
      const ttlExpired = now.getTime() - start.getTime() > ttl;
      const isHydrated = modalRef.classList.contains('hydrated');
      if(ttlExpired || isHydrated) {
        clearInterval(interval);
        modalRef.setAttribute('open', 'true')
      }
    }, 10);
  }

  private createSlDialog(title: string, options?: Options): SlDialogRef {
    const dialog = document.createElement('sl-dialog');
    dialog.setAttribute('label', title);
    dialog.setAttribute('header-style', options?.headerStyle ?? 'normal');

    let cssText = '';
    if(options?.width != null) {
      cssText += `--width: ${options.width};`
    }

    if(options?.padding === 'none') {
      cssText += `--padding: 0px;`
    }
    dialog.style.cssText = cssText;
    document.body.appendChild(dialog);
    return dialog as SlDialogRef;
  }

  private buildDisplayString(title: string, text: string) {
    let displayString: string;
    if (title != null) {
      displayString = `${title} - ${text}`;
    } else {
      displayString = text;
    }
    return displayString;
  }
}